import { format } from 'date-fns';
import { capitalize } from 'lodash-es';
import type { Ref } from 'vue';
import type { LocationQuery, LocationQueryValue, RouteLocationRaw } from 'vue-router';

import type {
  Api,
  RestorePointContentDetailsResponse,
  RestorePointDetailsResponse,
} from '@/api-client/api';
import type BadgesOverflow from '@/components/BadgesOverflow.vue';
import constants from '@/constants';
import { dateFormat } from '@/filters';
import type { KaBadgeProps } from '@/kasten-components';
import * as log from '@/logger';
import {
  PAGE_SIZE,
  RESTORE_POINTS_ARRAY_FILTER_VALUE,
} from '@/pages/restore-points-catalog/constants';
import { exportProfileFromLabels, importProfileFromLabels } from '@/utils/fetch-utils';
import { pluralizeWord } from '@/utils/format-utils';
import IconCalendarPlus2 from '~icons/lucide/calendar-plus-2';
import IconCloudCog from '~icons/lucide/cloud-cog';
import IconCloudUpload from '~icons/lucide/cloud-upload';
import IconDownload from '~icons/lucide/download';
import IconMonitorCog from '~icons/lucide/monitor-cog';
import IconScan from '~icons/lucide/scan';
import IconScroll from '~icons/lucide/scroll';
import IconServer from '~icons/lucide/server';
import IconShipWheel from '~icons/lucide/ship-wheel';
import IconTriangleAlert from '~icons/lucide/triangle-alert';
import IconWrench from '~icons/lucide/wrench';

import type { SortDirection } from '../profiles/location/types';
import {
  RESTORE_POINTS_ANY_FILTER_VALUE,
  RESTORE_POINTS_APP_NAME_FILTER_CLUSTER_SCOPED,
  RESTORE_POINTS_DEFAULT_FILTERS,
  RESTORE_POINTS_DEFAULT_SORT_BY,
  RESTORE_POINTS_DEFAULT_SORT_DIR,
  RESTORE_POINTS_QUERY_PARAMS_STORAGE_KEY,
} from './constants';
import { restorePointsRouteQueryParamsSchema } from './schemas';
import type {
  RestorePointAppType,
  RestorePointListItem,
  RestorePointsFilters,
  RestorePointsQueryKeys,
  RestorePointsQueryParams,
  RestorePointsSortBy,
  RestorePointType,
} from './types';

type KaBadgeOverflowProps = (typeof BadgesOverflow)['props'];

const DATE_FORMAT = 'yyyy-MM-dd HH:mm';

export const vmNameFromLabels = (labels?: Record<string, string>) => {
  if (!labels) return undefined;
  const appType = labels['k10.kasten.io/appType'];

  if (appType === constants.RESTORE_POINT_APP_TYPE_VM) {
    return labels['k10.kasten.io/appName'];
  }

  return undefined;
};

export const namespaceFromLabels = (labels?: Record<string, string>) => {
  if (!labels) return undefined;
  return labels['k10.kasten.io/appNamespace'];
};

export const expiresAtFromLabels = (labels?: Record<string, string>) => {
  if (!labels) return undefined;
  const raw = labels['k10.kasten.io/expiresAt'];
  if (!raw) return undefined;
  // K8s labels can't contain colons, so the time portion uses hyphens (see
  // go/src/kasten.io/k10/kio/names/utils.go — expiresAtLabelLayout).
  // Convert 2026-01-15T10-30-00Z → 2026-01-15T10:30:00Z for date-fns.
  return raw.replace(/T(\d{2})-(\d{2})-(\d{2})Z$/, 'T$1:$2:$3Z');
};

export const isPolicyManualRunFromLabels = (labels?: Record<string, string>) => {
  if (!labels) return false;
  return !!labels['k10.kasten.io/isRunNow'];
};

export const getRestorePointType = (restorePoint: RestorePointListItem): RestorePointType => {
  const labels = restorePoint.labels ?? {};

  if (exportProfileFromLabels(labels)) {
    return 'exported';
  }

  if (importProfileFromLabels(labels)) {
    return 'imported';
  }

  return 'snapshot';
};

/**
 * e.g. virtualMachine/namespace/vm, virtualMachine/namespace/*, virtualMachine/ * /vm
 */
export const buildVMAppRef = ({ namespace, vm }: { namespace?: string; vm?: string }) => {
  if (!namespace && !vm) return '';
  return `${constants.RESTORE_POINT_APP_TYPE_VM}/${namespace ?? '*'}/${vm ?? '*'}`;
};

export const buildRestorePointsActiveFilterBadges = ({
  appType,
  filters,
  setFilters,
  selectedNamespaces,
  selectedVMs,
}: {
  appType: RestorePointAppType;
  filters: RestorePointsFilters;
  setFilters: (values: Partial<RestorePointsFilters>) => void;
  selectedNamespaces: Ref<string[]>;
  selectedVMs: Ref<string[]>;
}): KaBadgeOverflowProps[] => {
  // Helper to extract namespaces or vmNames from appRefs
  // We need this, because selectedNamespaces and selectedVMs can contain only the names,
  // while appRefs are in full vm format (e.g. virtualMachine/namespace/vm)
  const extractNamesFromAppRefs = (type: RestorePointAppType, appRefs: string[]) => {
    const names: string[] = [];
    for (const ref of appRefs) {
      // e.g. virtualMachine/namespace/vm, virtualMachine/namespace/*, virtualMachine/*/vm
      const [, ns, vm] = ref.split('/');
      if (type === constants.RESTORE_POINT_APP_TYPE_NAMESPACE && ns !== '*') names.push(ns);
      if (type === constants.RESTORE_POINT_APP_TYPE_VM && vm !== '*') names.push(vm);
    }

    return [...new Set(names)];
  };

  const removeAppRefBadge = (type: RestorePointAppType, badgeLabel: string) => {
    const currentAppRefs = filters.appRefs;
    if (currentAppRefs === undefined) return;

    const isForNamespace = type === constants.RESTORE_POINT_APP_TYPE_NAMESPACE;
    const matchingAppRefs = currentAppRefs.filter((ref) => ref.includes(badgeLabel));
    const wildcardAppRefs: string[] = [];

    for (const appRef of matchingAppRefs) {
      // e.g. virtualMachine/namespace/vm, virtualMachine/namespace/*, virtualMachine/*/vm
      const [, ns, vm] = appRef.split('/');

      // EXAMPLE: If the appRef is a wildcard of vms (virtualMachine/ns/*),
      // that means we are dealing with namespace, so we can straight remove it.
      // Similar applies for ns wildcard.
      if (isForNamespace && vm === '*') {
        return (selectedNamespaces.value = extractNamesFromAppRefs(
          type,
          currentAppRefs.filter((ref) => ref !== appRef),
        ));
      }
      if (!isForNamespace && ns === '*') {
        return (selectedVMs.value = extractNamesFromAppRefs(
          type,
          currentAppRefs.filter((ref) => ref !== appRef),
        ));
      }

      // Otherwise we are dealing with a ns and vm combination, so we need to build a wildcard ref that will replace the matching ones
      wildcardAppRefs.push(
        isForNamespace ? buildVMAppRef({ vm }) : buildVMAppRef({ namespace: ns }),
      );
    }

    // Remove all matching refs from current appRefs
    const cleanedAppRefs = currentAppRefs.filter((ref) => !matchingAppRefs.includes(ref));

    // Determine if we need to add wildcard refs.
    const shouldAddWildcards =
      // If cleanedAppRefs is empty, it means that currentAppRefs contained only matching refs,
      // so we need to add the wildcard refs to avoid removing all refs
      cleanedAppRefs.length === 0
        ? true
        : // Otherwise check if any of the wildcard refs are already present
          cleanedAppRefs.some((ref) => {
            const [, ns, vm] = ref.split('/');

            return isForNamespace ? ns === '*' : vm === '*';
          });

    const result = [...cleanedAppRefs, ...(shouldAddWildcards ? wildcardAppRefs : [])];

    // Set selected namespaces and vms based on result.
    // Result do not contain deleted refs, so we need to filter to keep only those that are still selected.
    selectedNamespaces.value = extractNamesFromAppRefs('namespace', result).filter((ref) =>
      selectedNamespaces.value.includes(ref),
    );
    selectedVMs.value = extractNamesFromAppRefs('virtualMachine', result).filter((ref) =>
      selectedVMs.value.includes(ref),
    );
  };

  const namespaceBadge = (appRef: string): KaBadgeOverflowProps => ({
    left: { icon: IconShipWheel },
    color: 'blue' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: appRef,
    tooltip: 'Application',
    removeable: true,
    onRemove: () => {
      if (appType === constants.RESTORE_POINT_APP_TYPE_NAMESPACE) {
        selectedNamespaces.value = filters.appRefs?.filter((ref) => ref !== appRef) ?? [];
      }

      if (appType === constants.RESTORE_POINT_APP_TYPE_VM) {
        removeAppRefBadge('namespace', appRef);
      }
    },
  });

  const virtualMachineBadge = (appRef: string): KaBadgeOverflowProps => ({
    left: { icon: IconMonitorCog },
    color: 'blue' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: appRef,
    tooltip: 'Virtual Machine',
    removeable: true,
    onRemove: () => removeAppRefBadge('virtualMachine', appRef),
  });

  const clusterScopedBadge: KaBadgeOverflowProps = {
    left: { icon: IconServer },
    color: 'blue' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: RESTORE_POINTS_APP_NAME_FILTER_CLUSTER_SCOPED,
    tooltip: 'Include cluster-scoped restore points',
    removeable: true,
    onRemove: () =>
      (selectedNamespaces.value =
        filters.appRefs?.filter((ref) => ref !== RESTORE_POINTS_APP_NAME_FILTER_CLUSTER_SCOPED) ??
        []),
  };

  // Badges for namespaces and VMs
  const appRefBadges = (): KaBadgeOverflowProps[] => {
    if (!filters.appRefs) return [];

    const badges = filters.appRefs.flatMap((ref) => {
      // Badge for "cluster-scoped" filter
      if (ref === RESTORE_POINTS_APP_NAME_FILTER_CLUSTER_SCOPED) return [clusterScopedBadge];

      // Badges for VMs and Namespaces combined filter
      // e.g. virtualMachine/namespace/vm, virtualMachine/namespace/*, virtualMachine/*/vm
      if (ref.startsWith(`${constants.RESTORE_POINT_APP_TYPE_VM}/`)) {
        const [, ns, vm] = ref.split('/');
        const badgesStack: KaBadgeOverflowProps[] = [];
        if (ns !== '*') badgesStack.push(namespaceBadge(ns));
        if (vm !== '*') badgesStack.push(virtualMachineBadge(vm));

        return badgesStack;
      }

      // Badge for Namespace filter
      return [namespaceBadge(ref)];
    });

    // Return unique badges only
    const uniqueBadges = [...new Map(badges.map((b) => [b.label, b])).values()];

    // Sort badges to have consistent order: cluster-scoped, application, virtual machine, rest
    return uniqueBadges.sort((a, b) => {
      const score = (badge: KaBadgeOverflowProps) => {
        if (badge.tooltip === 'Include cluster-scoped restore points') return 0;
        if (badge.tooltip === 'Application') return 1;
        if (badge.tooltip === 'Virtual Machine') return 2;
        return 3;
      };

      return score(a) - score(b);
    });
  };

  const typeBadges = (): KaBadgeOverflowProps[] => {
    if (!filters.types || filters.types === (RESTORE_POINTS_ANY_FILTER_VALUE as RestorePointType)) {
      return [];
    }
    const type = filters.types;
    return [
      {
        left: {
          icon:
            type === 'exported' ? IconCloudUpload : type === 'imported' ? IconDownload : IconScan,
        },
        color: 'gray' as KaBadgeProps['color'],
        class: 'text-nowrap',
        label: capitalize(type),
        tooltip: 'Type',
        removeable: true,
        onRemove: () => setFilters({ types: RESTORE_POINTS_ANY_FILTER_VALUE }),
      },
    ];
  };

  const policyBadges = (): KaBadgeOverflowProps[] => {
    if (!filters.policyNames) return [];
    return filters.policyNames.map((policyName) => ({
      left: { icon: IconScroll },
      color: 'violet' as KaBadgeProps['color'],
      class: 'text-nowrap',
      label: policyName,
      tooltip: 'Policy',
      removeable: true,
      onRemove: () =>
        setFilters({ policyNames: filters.policyNames?.filter((n) => n !== policyName) }),
    }));
  };

  const profileBadges = (): KaBadgeOverflowProps[] => {
    if (!filters.profileNames) return [];
    return filters.profileNames.map((profileName) => ({
      left: { icon: IconCloudCog },
      color: 'cyan' as KaBadgeProps['color'],
      class: 'text-nowrap',
      label: profileName,
      tooltip: 'Profile',
      removeable: true,
      onRemove: () =>
        setFilters({ profileNames: filters.profileNames?.filter((n) => n !== profileName) }),
    }));
  };

  const createdAfterBadge: KaBadgeOverflowProps = {
    left: { icon: IconCalendarPlus2 },
    color: 'gray' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: filters.createdAfter ? `> ${format(filters.createdAfter, DATE_FORMAT)}` : '',
    tooltip: 'Created after',
    removeable: true,
    onRemove: () => setFilters({ createdAfter: undefined }),
  };

  const createdBeforeBadge: KaBadgeOverflowProps = {
    left: { icon: IconCalendarPlus2 },
    color: 'gray' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: filters.createdBefore ? `< ${format(filters.createdBefore, DATE_FORMAT)}` : '',
    tooltip: 'Created before',
    removeable: true,
    onRemove: () => setFilters({ createdBefore: undefined }),
  };

  const createdAtRangeBadge: KaBadgeOverflowProps = {
    left: { icon: IconCalendarPlus2 },
    color: 'gray' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label:
      filters.createdAfter && filters.createdBefore
        ? `${format(filters.createdAfter, DATE_FORMAT)} - ${format(filters.createdBefore, DATE_FORMAT)}`
        : '',
    tooltip: 'Created at',
    removeable: true,
    onRemove: () => {
      setFilters({ createdAfter: undefined });
      setFilters({ createdBefore: undefined });
    },
  };

  const includeManualRunsOnlyBadge: KaBadgeOverflowProps = {
    left: { icon: IconWrench },
    color: 'violet' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: '(manual)',
    tooltip: 'Include manual runs only',
    removeable: true,
    onRemove: () => setFilters({ includeManualRunsOnly: undefined }),
  };

  const successfulWithExceptionsBadge: KaBadgeOverflowProps = {
    left: { icon: IconTriangleAlert },
    color: 'orange' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: 'Successful With Exceptions',
    removeable: true,
    onRemove: () => setFilters({ successfulWithExceptions: undefined }),
  };

  const noExpirationBadge: KaBadgeOverflowProps = {
    left: { icon: IconTriangleAlert },
    color: 'orange' as KaBadgeProps['color'],
    class: 'text-nowrap',
    label: 'No expiration',
    tooltip: 'Expiration',
    removeable: true,
    onRemove: () => setFilters({ noExpiration: undefined }),
  };

  return [
    ...appRefBadges(),
    ...typeBadges(),
    ...policyBadges(),
    ...profileBadges(),
    ...(filters.createdAfter && !filters.createdBefore ? [createdAfterBadge] : []),
    ...(!filters.createdAfter && filters.createdBefore ? [createdBeforeBadge] : []),
    ...(filters.createdAfter && filters.createdBefore ? [createdAtRangeBadge] : []),
    ...(filters.noExpiration ? [noExpirationBadge] : []),
    ...(filters.includeManualRunsOnly ? [includeManualRunsOnlyBadge] : []),
    ...(filters.successfulWithExceptions ? [successfulWithExceptionsBadge] : []),
  ];
};

export const buildRestorePointsAriaLiveAnnouncement = ({
  count,
  filters,
  selectedNamespaces,
  selectedVMs,
}: {
  count: number;
  filters: RestorePointsFilters;
  selectedNamespaces: string[];
  selectedVMs: string[];
}) => {
  const { types, policyNames, profileNames, createdAfter, createdBefore, noExpiration } = filters;

  const filterParts: string[] = [];

  if (selectedNamespaces.length) {
    filterParts.push(
      `${pluralizeWord(selectedNamespaces.length, 'namespace', 'namespaces')} "${selectedNamespaces.join(', ')}"`,
    );
  }
  if (selectedVMs.length) {
    filterParts.push(
      `${pluralizeWord(selectedVMs.length, 'virtual machine', 'virtual machines')} "${selectedVMs.join(', ')}"`,
    );
  }

  if (types) {
    filterParts.push(`type "${types}"`);
  }

  if (policyNames?.length) {
    filterParts.push(
      `${pluralizeWord(policyNames.length, 'policy', 'policies')} "${policyNames.join(', ')}"`,
    );
  }

  if (profileNames?.length) {
    filterParts.push(
      `${pluralizeWord(profileNames.length, 'profile')} "${profileNames.join(', ')}"`,
    );
  }

  if (createdAfter) {
    filterParts.push(`created after ${dateFormat(createdAfter)}`);
  }

  if (createdBefore) {
    filterParts.push(`created before ${dateFormat(createdBefore)}`);
  }

  if (noExpiration) {
    filterParts.push('without expiration only');
  }

  if (filters.successfulWithExceptions) {
    filterParts.push('completed with exceptions');
  }

  if (!filterParts.length) {
    return '';
  }

  if (count > 0) {
    return `Displaying ${count} restore points matching: ${filterParts.join(', ')}`;
  }

  return `No restore points found matching: ${filterParts.join(', ')}`;
};

type RestorePointsQueryData = {
  [key in RestorePointsQueryKeys]:
    | LocationQueryValue
    | LocationQueryValue[]
    | boolean
    | number
    | undefined;
};

type RestorePointsQueryParsed = {
  filters: Partial<RestorePointsFilters>;
  sortBy?: RestorePointsSortBy;
  sortDir?: SortDirection;
};

const parseRestorePointsQueryData = (
  queryData: RestorePointsQueryData,
): RestorePointsQueryParsed => {
  const { data, error } = restorePointsRouteQueryParamsSchema.safeParse(queryData);

  if (error) {
    log.warn('Failed to parse restore points query data', error);
  }

  const {
    sortBy,
    sortDir,
    appRefs,
    policyNames,
    profileNames,
    types,
    offset: _offset,
    limit: _limit,
    ...filters
  } = data ?? {};

  return {
    filters: {
      ...filters,
      appRefs: appRefs ?? RESTORE_POINTS_ARRAY_FILTER_VALUE,
      profileNames: profileNames ?? RESTORE_POINTS_ARRAY_FILTER_VALUE,
      policyNames: policyNames ?? RESTORE_POINTS_ARRAY_FILTER_VALUE,
      types: types ?? RESTORE_POINTS_ANY_FILTER_VALUE,
    },
    sortBy,
    sortDir,
  };
};

const parseRestorePointsQuery = (query: LocationQuery): RestorePointsQueryParsed => {
  const toArray = (value: LocationQueryValue | LocationQueryValue[]): LocationQueryValue[] =>
    value ? (Array.isArray(value) ? value : [value]) : [];

  const toBool = (value: LocationQueryValue | LocationQueryValue[]) => {
    if (!value || Array.isArray(value)) return undefined;
    return value === 'true';
  };

  const queryData = {
    appType: query.appType,
    appRefs: toArray(query.appRefs),
    types: query.types,
    policyNames: toArray(query.policyNames),

    /**
     * TODO: see "transformRestorePointsQueryParams" below
     */
    // types: toArray(query.types),

    profileNames: toArray(query.profileNames),
    noExpiration: toBool(query.noExpiration),
    includeManualRunsOnly: toBool(query.includeManualRunsOnly),
    successfulWithExceptions: toBool(query.successfulWithExceptions),
    createdAfter: query.createdAfter,
    createdBefore: query.createdBefore,
    sortBy: query.sortBy,
    sortDir: query.sortDir,
    offset: 0,
    limit: PAGE_SIZE,
  } satisfies RestorePointsQueryData;

  return parseRestorePointsQueryData(queryData);
};

const RESTORE_POINTS_QUERY_KEYS = new Set(Object.keys(restorePointsRouteQueryParamsSchema.shape));

export const resolveRestorePointsQueryParams = (
  query: LocationQuery,
): Required<RestorePointsQueryParsed> => {
  /**
   * The URL is the source of truth. If there are recognized filter/sort params, use those.
   * Unknown params (e.g. from other features) are ignored so localStorage still loads.
   */
  const parsed = parseRestorePointsQuery(query);
  const hasKnownQueryParams = Object.keys(query).some((key) => RESTORE_POINTS_QUERY_KEYS.has(key));

  if (hasKnownQueryParams) {
    return {
      ...parsed,
      sortBy: parsed.sortBy ?? RESTORE_POINTS_DEFAULT_SORT_BY,
      sortDir: parsed.sortDir ?? RESTORE_POINTS_DEFAULT_SORT_DIR,
    };
  }

  /**
   * Otherwise we want to preserve the last used query params, stored in local storage
   */
  const stored = localStorage.getItem(RESTORE_POINTS_QUERY_PARAMS_STORAGE_KEY);
  if (stored) {
    const data = JSON.parse(stored);
    const parsedStorage = parseRestorePointsQueryData(data);

    return {
      ...parsedStorage,
      sortBy: parsedStorage.sortBy ?? RESTORE_POINTS_DEFAULT_SORT_BY,
      sortDir: parsedStorage.sortDir ?? RESTORE_POINTS_DEFAULT_SORT_DIR,
    };
  }

  return {
    filters: RESTORE_POINTS_DEFAULT_FILTERS,
    sortBy: RESTORE_POINTS_DEFAULT_SORT_BY,
    sortDir: RESTORE_POINTS_DEFAULT_SORT_DIR,
  };
};

/**
 * Legacy VBR redirect links use the `/restore/:namespace` route with an optional
 * `filter=<policyName>` query param (e.g. `/restore/tkc1-2-1?filter=appsandclusterscope`).
 * The new Restore Points page lives at the `RestorePointsCatalog` route and drives its
 * filters from `appType` / `appRefs` / `policyNames` query params instead. This maps the
 * legacy params onto the new query so the filters pre-apply as before:
 * - the `:namespace` segment becomes an `appRefs` namespace entry, except the Go backend's
 *   cluster-scoped sentinel (`kasten-io-cluster`) which maps to the `cluster-scoped` option;
 * - the legacy `filter=<policyName>` becomes a `policyNames` entry.
 */
export const buildLegacyRestoreRedirect = (
  namespace: string,
  filter: LocationQueryValue | LocationQueryValue[] | undefined,
): RouteLocationRaw => {
  const query: LocationQuery = {
    appType: constants.RESTORE_POINT_APP_TYPE_NAMESPACE,
    appRefs:
      namespace === constants.NS_CLUSTER_RESOURCES
        ? RESTORE_POINTS_APP_NAME_FILTER_CLUSTER_SCOPED
        : namespace,
  };

  if (typeof filter === 'string' && filter.length > 0) {
    query.policyNames = filter;
  }

  return { name: 'RestorePointsCatalog', query };
};

// TODO: Investigate why this is needed.
// On local, and when deployed to a cluster using build.sh, the restore point names are not prefixed with namespace.
// On test env they are. We need them prefixed.
export const includeAppInRestorePointName = (rp: RestorePointListItem) => {
  const app = namespaceFromLabels(rp.labels);

  if (!app) {
    return rp.name;
  }

  if (rp.name?.startsWith(app)) {
    return rp.name;
  }

  return `${app}-${rp.name}`;
};

// TODO: Maybe we should move this FirstArg type to some shared types file?
export type FirstArg<T> = T extends (arg1: infer A, ...args: any[]) => any ? A : never;
type ListRestorePointsApiEndpoint = Api<any, any, any>['bff']['listRestorePoints'];
type ListRestorePointsQueryParams = FirstArg<ListRestorePointsApiEndpoint>;

export const transformRestorePointsQueryParams = (
  params: RestorePointsQueryParams,
): ListRestorePointsQueryParams => {
  const { successfulWithExceptions, ...restParams } = params;

  const includeClusterScoped = params.appRefs?.includes(
    RESTORE_POINTS_APP_NAME_FILTER_CLUSTER_SCOPED,
  )
    ? true
    : undefined;

  const appRefs = includeClusterScoped
    ? params.appRefs?.filter((appRef) => appRef !== RESTORE_POINTS_APP_NAME_FILTER_CLUSTER_SCOPED)
    : params.appRefs;

  const types =
    params.types === RESTORE_POINTS_ANY_FILTER_VALUE ? undefined : params.types || undefined;

  const policyNames =
    params.policyNames?.length === 0 ? undefined : params.policyNames || undefined;

  const createdAfter = params.createdAfter
    ? new Date(params.createdAfter).toISOString()
    : undefined;

  const createdBefore = params.createdBefore
    ? new Date(params.createdBefore).toISOString()
    : undefined;

  return {
    ...restParams,

    includeClusterScoped,

    createdAfter,
    createdBefore,

    appRefs,
    // @ts-expect-error
    types,

    policyNames,

    hasExceptions: successfulWithExceptions,
  };
};

export const getPotentialRPIssuesWarningMessage = (
  restorePoint: RestorePointContentDetailsResponse | RestorePointDetailsResponse,
  isClusterResources: boolean,
) => {
  const restorePointType = getRestorePointType({
    labels: restorePoint.metadata?.labels,
    isClusterRestorePoint: isClusterResources,
  });

  if (restorePointType === 'snapshot') {
    return restorePoint.warning ?? undefined;
  }

  return undefined;
};
